import { useMemo } from 'react';

import { useQueries, useQuery, type UseQueryResult } from '@tanstack/react-query';
import { isAxiosError } from 'axios';

import type { PERIOD_TYPE } from 'constants/charting';
import { createApiClient } from 'lib/api/createApiClient';
import type {
    GetForecastResponse,
    GetMarketHistoricalDataRequest,
    HistoricalDataPeriodInterval,
    HistoricalForecasts,
    HistoricalMarketCandlestickStats,
    MarketCandlestickVo,
    MarketForecastHistoryVo,
} from 'models/historical_data';
import type { GetMarkeCandlesticksResponse } from 'models/reqres';
import type { HistoricalMarketStats } from 'models/stats';
import type { MarketEntity } from 'queries/marketEntityTransform';
import {
    emptyCandlestickStatsHistory,
    emptyStatsHistory,
    extrapolateStatsHistory,
    isMarketOpen,
    mapCandlesticksToCandlesticksStatsHistory,
    mapCandlesticksToStatsHistory,
} from 'queries/marketEntityTransform';
import { asyncMapForecastsHistory, getHistoricalDataParams } from 'utils/charting';
import config from 'utils/config';
import { buildQueryString } from 'utils/query';

/**
 * Event-level forecast-history + per-market candlestick-history hydration, feeding
 * `RottenTomatoesEventHeader`'s "Kalshi forecast" stat. Additive REST+polling
 * infrastructure alongside `Event.loadEventForecastHistory`/`BinaryMarket.loadCandlesticksHistory` —
 * part of the phased `MarketStore` removal (see `~/.claude/plans/create-a-plan-to-mutable-conway.md`).
 *
 * Also has a per-MARKET forecast-history hook (`useMarketForecastHistoryExtrapolated`), the RQ
 * replacement for `BinaryMarket.loadMarketForecastHistory`/`startPollingForecastsHistory` —
 * additive infra for `MarketPriceHistoryGraphWrapper.tsx`/`ForecastMarketWidget.tsx`'s eventual
 * cutover, not yet wired into either.
 *
 * `queries/forecastHistory.ts` ALSO fetches per-market `/markets/{id}/forecast_history` — for a
 * different consumer (`EventPriceHistoriesGraph.tsx`'s multi-market chart lines) via a different
 * mechanism (`fetchServerSide`, a hardcoded `candlestick_function`, no extrapolation step; built
 * for batched `useQueries` across N markets with a proven live/game failure-backoff model). That
 * file is left untouched deliberately: its consumer needs raw un-extrapolated per-market series
 * for direct charting, not a single market's `forecastHistoryExtrapolated`, and reshaping it to
 * also serve this file's shape would be a bigger, separate reconciliation, not a same-shape
 * additive hook. This file's version reuses this file's own `createApiClient`/axios convention
 * (matching `fetchEventForecastHistory`/`fetchMarketCandlesticksHistory` below) and needs the
 * extrapolation step, since its target consumers read `forecastHistoryExtrapolated` directly.
 */

const api = createApiClient({ baseURL: `${config.exchangeApiUrl}/v1` });

const ONE_MINUTE = 60;

const isValidHistoricalDataParams = (startTs: number, endTs: number): boolean =>
    !!startTs && !isNaN(startTs) && !!endTs && !isNaN(endTs) && endTs > startTs;

/**
 * 404/409 means no forecast series exists for this event yet (mirrors
 * `Event.loadEventForecastHistory`'s `error.notFound || error.conflict` fallback) — expected,
 * not a failure. Resolve empty rather than let it retry/settle into `isError`.
 */
const isNoForecastHistoryError = (error: unknown): boolean =>
    isAxiosError(error) && (error.response?.status === 404 || error.response?.status === 409);

/* -------------------------------------------------------------------------- */
/*                          Event-level forecast history                      */
/* -------------------------------------------------------------------------- */

export const eventForecastHistoryKeys = {
    all: ['eventForecastHistory'] as const,
    byEvent: (seriesTicker: string, eventTicker: string, startTs: number, endTs: number, periodInterval: string) =>
        [...eventForecastHistoryKeys.all, seriesTicker, eventTicker, startTs, endTs, periodInterval] as const,
};

export async function fetchEventForecastHistory(
    seriesTicker: string,
    eventTicker: string,
    params: GetMarketHistoricalDataRequest,
    signal?: AbortSignal,
): Promise<MarketForecastHistoryVo[]> {
    const response = await api.get<GetForecastResponse>(
        `/series/${seriesTicker}/events/${eventTicker}/forecast_history${buildQueryString(params)}`,
        { signal },
    );
    return response.data.forecast_history;
}

export interface UseEventForecastHistoryOptions {
    enabled?: boolean;
}

/** Mirrors `Event.loadEventForecastHistory`'s polling cadence, backed by RQ refetching. */
export function useEventForecastHistoryExtrapolated(
    seriesTicker: string,
    eventTicker: string,
    openDate: string,
    closeDate: string,
    period: PERIOD_TYPE = 'all',
    options: UseEventForecastHistoryOptions = {},
): UseQueryResult<HistoricalForecasts[]> {
    // Memoized so the query key is stable within a minute boundary (live endTs rounds to the
    // current minute) — otherwise every render that crosses a minute would mint a fresh
    // queryKey instead of letting `refetchInterval` update the existing one.
    const { periodInterval, startTs, endTs } = useMemo(
        () => getHistoricalDataParams(period, openDate, closeDate),
        [period, openDate, closeDate],
    );
    const validParams = isValidHistoricalDataParams(startTs, endTs);
    const isLiveOrGame = period === 'live' || period === 'game';

    return useQuery({
        queryKey: eventForecastHistoryKeys.byEvent(seriesTicker, eventTicker, startTs, endTs, periodInterval),
        queryFn: async ({ signal }) => {
            // Live/game ranges depend on Date.now(); recompute on each refetch (mirrors
            // `queries/forecastHistory.ts`) so the window slides forward instead of the poller
            // re-requesting the range captured at mount.
            const range = isLiveOrGame
                ? getHistoricalDataParams(period, openDate, closeDate)
                : { startTs, endTs, periodInterval };
            let forecasts: MarketForecastHistoryVo[];
            try {
                forecasts = await fetchEventForecastHistory(
                    seriesTicker,
                    eventTicker,
                    { start_ts: range.startTs, end_ts: range.endTs, period_interval: range.periodInterval },
                    signal,
                );
            } catch (error) {
                if (isNoForecastHistoryError(error)) return [];
                throw error;
            }
            if (!forecasts.length) return [];
            return asyncMapForecastsHistory(forecasts);
        },
        enabled: !!seriesTicker && !!eventTicker && validParams && (options.enabled ?? true),
        refetchInterval: config.slowPollingInterval,
        // Don't keep polling forecast history in a backgrounded tab — a hidden live market/BTC tab
        // otherwise keeps issuing REST polls, inflating the resource count and keeping Datadog's
        // loading window open. The global QueryClient disables focus refetching
        // (refetchOnWindowFocus: false), so re-enable it here so a returning user gets fresh chart
        // data immediately on refocus instead of waiting up to a full interval for the next tick.
        // `true` (not `'always'`) refetches only when stale, and staleTime === interval.
        refetchIntervalInBackground: false,
        refetchOnWindowFocus: true,
        staleTime: config.slowPollingInterval,
    });
}

/* -------------------------------------------------------------------------- */
/*                          Market-level forecast history                     */
/* -------------------------------------------------------------------------- */

export const marketForecastHistoryKeys = {
    all: ['marketForecastHistory'] as const,
    byMarket: (
        seriesTicker: string,
        marketId: string,
        startTs: number,
        endTs: number,
        periodInterval: string,
        candlestickFunction: string | undefined,
    ) =>
        [
            ...marketForecastHistoryKeys.all,
            seriesTicker,
            marketId,
            startTs,
            endTs,
            periodInterval,
            candlestickFunction,
        ] as const,
};

/**
 * Mirrors `getMarketForecastHistory`'s (`transport/domains/markets.ts`) defensive re-rounding:
 * round both bounds down to the minute, then bump `start_ts` forward one more minute whenever
 * that still leaves it before `end_ts` — excludes a partial leading minute from the average.
 */
function reroundMarketForecastParams(params: GetMarketHistoricalDataRequest): GetMarketHistoricalDataRequest {
    const start = params.start_ts - (params.start_ts % ONE_MINUTE);
    const end = params.end_ts - (params.end_ts % ONE_MINUTE);
    return { ...params, start_ts: start < end ? start + ONE_MINUTE : start, end_ts: end };
}

export async function fetchMarketForecastHistory(
    seriesTicker: string,
    marketId: string,
    params: GetMarketHistoricalDataRequest,
    signal?: AbortSignal,
): Promise<MarketForecastHistoryVo[]> {
    const response = await api.get<GetForecastResponse>(
        `/series/${seriesTicker}/markets/${marketId}/forecast_history${buildQueryString(reroundMarketForecastParams(params))}`,
        { signal },
    );
    return response.data.forecast_history;
}

export interface UseMarketForecastHistoryOptions {
    enabled?: boolean;
    /** Omitted entirely from the request (not sent as `undefined`) when left unset. */
    candlestickFunction?: string;
}

/**
 * Mirrors `BinaryMarket.loadMarketForecastHistory`'s polling cadence, backed by RQ refetching.
 * `period === 'game'` polls at `slowPollingInterval`, everything else at
 * `midPollingInterval` — the one place in the whole forecast/candlestick mechanism where cadence
 * varies by period (the event-level and candlestick pollers are both fixed at `slowPollingInterval`).
 */
export function useMarketForecastHistoryExtrapolated(
    seriesTicker: string,
    marketId: string,
    openDate: string,
    closeDate: string,
    period: PERIOD_TYPE = 'all',
    options: UseMarketForecastHistoryOptions = {},
): UseQueryResult<HistoricalForecasts[]> {
    // Memoized so the query key is stable within a minute boundary (live endTs rounds to the
    // current minute) — otherwise every render that crosses a minute would mint a fresh
    // queryKey instead of letting `refetchInterval` update the existing one.
    const { periodInterval, startTs, endTs } = useMemo(
        () => getHistoricalDataParams(period, openDate, closeDate),
        [period, openDate, closeDate],
    );
    const validParams = isValidHistoricalDataParams(startTs, endTs);
    const pollingInterval = period === 'game' ? config.slowPollingInterval : config.midPollingInterval;
    const isLiveOrGame = period === 'live' || period === 'game';

    return useQuery({
        queryKey: marketForecastHistoryKeys.byMarket(
            seriesTicker,
            marketId,
            startTs,
            endTs,
            periodInterval,
            options.candlestickFunction,
        ),
        queryFn: async ({ signal }) => {
            // Live/game ranges depend on Date.now(); recompute on each refetch (mirrors
            // `queries/forecastHistory.ts`) so the window slides forward instead of the poller
            // re-requesting the range captured at mount.
            const range = isLiveOrGame
                ? getHistoricalDataParams(period, openDate, closeDate)
                : { startTs, endTs, periodInterval };
            let forecasts: MarketForecastHistoryVo[];
            try {
                forecasts = await fetchMarketForecastHistory(
                    seriesTicker,
                    marketId,
                    {
                        start_ts: range.startTs,
                        end_ts: range.endTs,
                        period_interval: range.periodInterval,
                        candlestick_function: options.candlestickFunction,
                    },
                    signal,
                );
            } catch (error) {
                if (isNoForecastHistoryError(error)) return [];
                throw error;
            }
            if (!forecasts.length) return [];
            return asyncMapForecastsHistory(forecasts);
        },
        enabled: !!seriesTicker && !!marketId && validParams && (options.enabled ?? true),
        refetchInterval: pollingInterval,
        // See note above: pause polling while backgrounded, but refetch fresh on refocus
        // (refetchOnWindowFocus overrides the global false; refetches only when stale).
        refetchIntervalInBackground: false,
        refetchOnWindowFocus: true,
        staleTime: pollingInterval,
    });
}

/* -------------------------------------------------------------------------- */
/*                 Per-market candlestick history (non-extrapolated)          */
/* -------------------------------------------------------------------------- */

export const marketCandlestickHistoryKeys = {
    all: ['marketCandlestickHistory'] as const,
    /**
     * `shape` discriminates `useMarketsNonExtrapolatedStatsHistory` (`'statsOnly'`, returns a
     * plain `HistoricalMarketStats[]`) from `useMarketsCandlestickChartHistory` (`'chart'`,
     * returns `{ statsHistory, candlestickStatsHistory }`). Both hooks can otherwise derive an
     * identical `seriesTicker`/`marketId`/`startTs`/`endTs`/`periodInterval` tuple for the same
     * market (e.g. a single-market event, where the event-level window equals the market's own
     * window) and are mounted together on the same event page — without this discriminator they'd
     * collide on one cache entry and whichever hook populated it first would silently hand its
     * shape to the other.
     */
    byMarket: (
        shape: 'statsOnly' | 'chart',
        seriesTicker: string,
        marketId: string,
        startTs: number,
        endTs: number,
        periodInterval: HistoricalDataPeriodInterval,
    ) => [...marketCandlestickHistoryKeys.all, shape, seriesTicker, marketId, startTs, endTs, periodInterval] as const,
};

/** Mirrors `getMarketCandlesticksHistory`'s defensive minute-boundary rerounding of `start_ts`/`end_ts`. */
export async function fetchMarketCandlesticksHistory(
    seriesTicker: string,
    marketId: string,
    params: GetMarketHistoricalDataRequest,
    signal?: AbortSignal,
): Promise<MarketCandlestickVo[]> {
    const reroundedParams: GetMarketHistoricalDataRequest = {
        ...params,
        start_ts: params.start_ts - (params.start_ts % ONE_MINUTE),
        end_ts: params.end_ts - (params.end_ts % ONE_MINUTE),
    };
    const response = await api.get<GetMarkeCandlesticksResponse>(
        `/series/${seriesTicker}/markets/${marketId}/candlesticks${buildQueryString(reroundedParams)}`,
        { signal },
    );
    return response.data.candlesticks;
}

/**
 * Fetches + maps each of `markets`' candlestick history to its pre-extrapolation
 * `HistoricalMarketStats[]` (mirrors `BinaryMarket.nonExtrapolatedStatsHistory`), keyed by
 * ticker. `useQueries` (not a per-market hook call) since the market count varies —
 * mirrors the batched pattern in `queries/milestones.ts`'s `useMilestonesByIds`.
 */
export function useMarketsNonExtrapolatedStatsHistory(
    seriesTicker: string,
    markets: MarketEntity[],
    openDate: string,
    closeDate: string,
    period: PERIOD_TYPE = 'all',
    options: UseEventForecastHistoryOptions = {},
): { statsHistoryByTicker: Record<string, HistoricalMarketStats[]>; allCandlesticksLoaded: boolean } {
    // Memoized so the query key is stable within a minute boundary (live endTs rounds to the
    // current minute) — otherwise every render that crosses a minute would mint a fresh
    // queryKey instead of letting `refetchInterval` update the existing one.
    const { periodInterval, startTs, endTs } = useMemo(
        () => getHistoricalDataParams(period, openDate, closeDate),
        [period, openDate, closeDate],
    );
    const validParams = isValidHistoricalDataParams(startTs, endTs);
    const enabled = !!seriesTicker && validParams && (options.enabled ?? true);
    const isLiveOrGame = period === 'live' || period === 'game';

    const results = useQueries({
        queries: markets.map((market) => ({
            queryKey: marketCandlestickHistoryKeys.byMarket(
                'statsOnly',
                seriesTicker,
                market.id,
                startTs,
                endTs,
                periodInterval,
            ),
            queryFn: async ({ signal }: { signal: AbortSignal }) => {
                // Live/game ranges depend on Date.now(); recompute on each refetch (mirrors
                // `queries/forecastHistory.ts`) so the window slides forward instead of the poller
                // re-requesting the range captured at mount.
                const range = isLiveOrGame
                    ? getHistoricalDataParams(period, openDate, closeDate)
                    : { startTs, endTs, periodInterval };
                const candlesticks = await fetchMarketCandlesticksHistory(
                    seriesTicker,
                    market.id,
                    { start_ts: range.startTs, end_ts: range.endTs, period_interval: range.periodInterval },
                    signal,
                );
                // Mirrors `BinaryMarket`'s placeholder: `eventSmoothedHistoricalMeans` flattens every
                // market's timestamps and takes Math.min/max over them, so an empty array here (rather
                // than a one-point placeholder) would produce NaN/invalid-date results once
                // `allCandlesticksLoaded` flips true.
                if (!candlesticks.length) return emptyStatsHistory();
                return mapCandlesticksToStatsHistory(candlesticks, range.periodInterval);
            },
            enabled: enabled && !!market.id,
            refetchInterval: config.slowPollingInterval,
            // See note above: pause polling while backgrounded, but refetch fresh on refocus
            // (refetchOnWindowFocus overrides the global false; refetches only when stale).
            refetchIntervalInBackground: false,
            refetchOnWindowFocus: true,
            staleTime: config.slowPollingInterval,
        })),
    });

    const statsHistoryByTicker: Record<string, HistoricalMarketStats[]> = {};
    markets.forEach((market, i) => {
        const data = results[i]?.data;
        if (data) statsHistoryByTicker[market.ticker] = data;
    });

    return {
        statsHistoryByTicker,
        allCandlesticksLoaded: markets.length > 0 && results.every((r) => r.isSuccess),
    };
}

/* -------------------------------------------------------------------------- */
/*      Per-market candlestick chart history (full ChartsPanel replacement)   */
/* -------------------------------------------------------------------------- */

export interface MarketCandlestickChartHistory {
    /** Extrapolated line-chart ladder — mirrors `BinaryMarket.statsHistory` post-`extrapolateStatsHistory`. */
    statsHistory: HistoricalMarketStats[];
    /** Raw OHLC ladder — mirrors `BinaryMarket.candlestickStatsHistory` (never extrapolated). */
    candlestickStatsHistory: HistoricalMarketCandlestickStats[];
    isLoading: boolean;
}

/**
 * Full per-market candlestick chart history for `ChartsPanel.tsx` — the RQ replacement for
 * `BinaryMarket.startPollingCandlesticksHistory`/`loadCandlesticksHistory`'s data-fetching half
 * (the resampling half is `marketSampledHistory` in `queries/marketEntityTransform.ts`). Batched
 * via `useQueries` (like `useMarketsNonExtrapolatedStatsHistory` above) since `ChartsPanel` needs
 * a dynamically-selected main+comparison market set, but — unlike that hook — uses each market's
 * OWN `openDate`/`closeDate` rather than a shared event-level window, matching `sampledHistory`'s
 * per-market `openTsSeconds`/`closeTsSeconds` usage.
 *
 * Polls at `slowPollingInterval` per market while that market is open; fetches once for closed
 * markets — mirrors `startPollingCandlesticksHistory`'s
 * open/closed branch in `ChartsPanel.tsx`'s original effect.
 */
export function useMarketsCandlestickChartHistory(
    seriesTicker: string,
    markets: MarketEntity[],
    period: PERIOD_TYPE,
    options: UseEventForecastHistoryOptions = {},
): Record<string, MarketCandlestickChartHistory> {
    const enabledOption = options.enabled ?? true;
    const isLiveOrGame = period === 'live' || period === 'game';

    // Memoized per market-id so the query key is stable within a minute boundary (live endTs
    // rounds to the current minute) — otherwise any render that crosses a minute would mint a
    // fresh key for every market instead of letting `refetchInterval` update the existing entry.
    // Unlike the other hooks here, each market has its OWN openDate/closeDate rather than a
    // shared event-level window, so the memo key is derived per-market rather than a flat tuple.
    const rangesKey = markets.map((market) => `${market.id}:${market.openDate}:${market.closeDate}`).join('|');
    const rangesByMarketId = useMemo(
        () =>
            new Map(
                markets.map((market) => [
                    market.id,
                    getHistoricalDataParams(period, market.openDate, market.closeDate),
                ]),
            ),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [period, rangesKey],
    );

    const results = useQueries({
        queries: markets.map((market) => {
            const { periodInterval, startTs, endTs } =
                rangesByMarketId.get(market.id) ?? getHistoricalDataParams(period, market.openDate, market.closeDate);
            const validParams = isValidHistoricalDataParams(startTs, endTs);
            return {
                queryKey: marketCandlestickHistoryKeys.byMarket(
                    'chart',
                    seriesTicker,
                    market.id,
                    startTs,
                    endTs,
                    periodInterval,
                ),
                queryFn: async ({ signal }: { signal: AbortSignal }) => {
                    // Live/game ranges depend on Date.now(); recompute on each refetch (mirrors
                    // `queries/forecastHistory.ts`) so the window slides forward instead of the
                    // poller re-requesting the range captured at mount.
                    const range = isLiveOrGame
                        ? getHistoricalDataParams(period, market.openDate, market.closeDate)
                        : { startTs, endTs, periodInterval };
                    const candlesticks = await fetchMarketCandlesticksHistory(
                        seriesTicker,
                        market.id,
                        { start_ts: range.startTs, end_ts: range.endTs, period_interval: range.periodInterval },
                        signal,
                    );
                    if (!candlesticks.length) {
                        return {
                            statsHistory: emptyStatsHistory(),
                            candlestickStatsHistory: emptyCandlestickStatsHistory(),
                        };
                    }
                    const rawStatsHistory = mapCandlesticksToStatsHistory(candlesticks, range.periodInterval);
                    return {
                        statsHistory: extrapolateStatsHistory(rawStatsHistory, range.periodInterval),
                        candlestickStatsHistory: mapCandlesticksToCandlesticksStatsHistory(
                            candlesticks,
                            range.periodInterval,
                        ),
                    };
                },
                enabled: !!seriesTicker && !!market.id && validParams && enabledOption,
                refetchInterval: isMarketOpen(market) ? config.slowPollingInterval : false,
                // See note above: pause polling while backgrounded, but refetch fresh on refocus
                // (refetchOnWindowFocus overrides the global false; refetches only when stale).
                refetchIntervalInBackground: false,
                refetchOnWindowFocus: true,
                staleTime: config.slowPollingInterval,
            };
        }),
    });

    const historyByTicker: Record<string, MarketCandlestickChartHistory> = {};
    markets.forEach((market, i) => {
        const result = results[i];
        historyByTicker[market.ticker] = {
            statsHistory: result?.data?.statsHistory ?? emptyStatsHistory(),
            candlestickStatsHistory: result?.data?.candlestickStatsHistory ?? emptyCandlestickStatsHistory(),
            isLoading: result?.isLoading ?? false,
        };
    });

    return historyByTicker;
}
